import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { API_BASE_URL } from '@/api/api';
import { HttpStatus } from '@/types/httpStatus';

import { ApiTags } from './tags';

let getAccessTokenSilently: (() => Promise<string | undefined>) | null = null;
let logoutFn: (() => void) | null = null;
// Set once a forced sign-out has started, so requests that fail together sign the user out once
let signingOut = false;

export const setAuthTokenGetter = (
  getter: () => Promise<string | undefined>
) => {
  getAccessTokenSilently = getter;
};

export const setLogoutHandler = (logout: () => void) => {
  logoutFn = logout;
  signingOut = false;
};

/**
 * The `error` of the CUSTOM_ERROR a query returns when no access token could be had for a reason
 * that may pass (a lost refresh lock, a network blip). No request was sent. errorLogger skips it,
 * so a polling query fails quietly and tries again on its next tick. A caller that shows its own
 * snackbar for a failed mutation gets `data.message`, never this code.
 */
export const NO_ACCESS_TOKEN = 'NO_ACCESS_TOKEN';

export const REQUEST_NOT_SENT_MESSAGE =
  'Your request could not be sent. Please try again.';

// Auth0 error codes that mean the session cannot be renewed without the user signing in again.
// Anything else (`timeout` from the cross-tab refresh lock, a network failure) may pass.
const SESSION_ENDED_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'missing_refresh_token',
  'mfa_required',
]);

export type TokenResult =
  | { kind: 'token'; token: string }
  | { kind: 'sessionEnded' }
  | { kind: 'unavailable' };

const isSessionEndedError = (err: unknown) => {
  const code = (err as { error?: unknown } | null)?.error;
  return typeof code === 'string' && SESSION_ENDED_ERRORS.has(code);
};

const tryGetToken = async (): Promise<TokenResult> => {
  if (!getAccessTokenSilently) return { kind: 'unavailable' };
  try {
    const token = await getAccessTokenSilently();
    // Auth0 answers with no token only once the session ceiling is reached, and then keeps doing so
    return token ? { kind: 'token', token } : { kind: 'sessionEnded' };
  } catch (err) {
    console.error('Could not get access token', err);
    return isSessionEndedError(err)
      ? { kind: 'sessionEnded' }
      : { kind: 'unavailable' };
  }
};

/** Asks for a token, and once more if the first answer may have been a passing failure. */
const resolveToken = async (): Promise<TokenResult> => {
  const first = await tryGetToken();
  return first.kind === 'unavailable' ? tryGetToken() : first;
};

/**
 * Ends the session after the backend rejected a fresh token, or Auth0 said the session is over.
 * The sign-in screen shows no message for it: a session that ends is expected, and an error
 * would read as something having gone wrong.
 */
const handleAuthFailure = () => {
  // Concurrent requests can each fail; one sign-out is enough
  if (signingOut) return;
  if (!logoutFn) {
    console.error('Authentication failed before a logout handler was set');
    return;
  }
  signingOut = true;
  logoutFn();
};

/**
 * Gets a token the way a request does, for a caller outside RTK Query: the app's first token at
 * startup. If Auth0 says the session is over, the user is signed out, as a failed request would.
 */
export const getTokenOrSignOut = async (): Promise<TokenResult> => {
  const result = await resolveToken();
  if (result.kind === 'sessionEnded') handleAuthFailure();
  return result;
};

const fetchWithToken = (token: string) =>
  fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: headers => {
      // Note: When an endpoint sends a FormData body, fetchBaseQuery automatically
      // strips this Content-Type header so the browser can set multipart/form-data
      // with the correct boundary. No special handling is needed here.
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  });

const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Never send a request without a token: the backend can only answer it with a 401.
  let tokenResult = await resolveToken();
  let result =
    tokenResult.kind === 'token'
      ? await fetchWithToken(tokenResult.token)(args, api, extraOptions)
      : undefined;

  // Retry once on 401 (expired token) with whatever token the getter gives now.
  // No token this time for a passing reason means the retry never happened, which
  // is not grounds to sign the user out.
  if (result?.error?.status === HttpStatus.UNAUTHORIZED) {
    tokenResult = await resolveToken();
    if (tokenResult.kind === 'token') {
      result = await fetchWithToken(tokenResult.token)(args, api, extraOptions);
    } else if (tokenResult.kind === 'unavailable') {
      result = undefined;
    }
  }

  // Distinguish the two auth-related statuses:
  //  - 401 UNAUTHORIZED after a retry, or Auth0 saying the session is over: the
  //    session is no longer valid → force a re-login.
  //  - 403 FORBIDDEN: the user is authenticated but not permitted to perform this
  //    specific request. This must NOT log them out. Let the query reject so the
  //    calling view degrades gracefully (e.g. renders an empty panel); clicking an
  //    action a role can't perform should surface an error, not end the session.
  //    (errorLogger already suppresses 403 from snackbars, so this stays silent.)
  if (
    tokenResult.kind === 'sessionEnded' ||
    result?.error?.status === HttpStatus.UNAUTHORIZED
  ) {
    handleAuthFailure();
    return { data: undefined };
  }

  if (!result) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: NO_ACCESS_TOKEN,
        data: { message: REQUEST_NOT_SENT_MESSAGE },
      },
    };
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: Object.values(ApiTags),
  endpoints: () => ({}),
});
